import { inject, Injectable } from '@angular/core';
import { MembershipActionTypes as RCV2MembershipActionType } from '@features/redeem-cash-v2/store/actions/membership.actions';
import { RewardsPreferenceActionTypes } from '@features/rewards-preference/store/actions';
import { CreateRewardsProgramPreferenceSuccess } from '@features/rewards-program/store/actions/rewards-program-preference.actions';
import { MembershipActionTypes as TCV2MembershipActionTypes } from '@features/transfer-connect-v2/store/actions';
import { UserInit } from '@features/user-init/user-init.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GuardhouseService } from '@rd/core/services/guardhouse/guardhouse.service';
import { HttpError, Membership } from '@rd/types';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { MembershipApiService } from '../../services/membership-api/membership-api.service';
import * as actions from '../actions/membership.actions';

@Injectable()
export class MembershipEffects {
  private guardhouseService = inject(GuardhouseService);

  constructor(
    private actions$: Actions,
    private membershipApiService: MembershipApiService,
  ) {}

  loadMemberships$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.LOAD_ALL_MEMBERSHIPS),
      switchMap(() => {
        return this.membershipApiService.getAllMemberships().pipe(
          map((memberships: Membership[]) => {
            return new actions.LoadAllMembershipsSuccess(memberships);
          }),
          catchError((error: HttpError) =>
            of(new actions.LoadAllMembershipsFail(error)),
          ),
        );
      }),
    ),
  );

  createMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.CREATE_MEMBERSHIP),
      switchMap((action: actions.CreateMembership) => {
        return this.membershipApiService.createMembership(action.payload).pipe(
          map((res) => {
            return new actions.CreateMembershipSuccess(res);
          }),
          catchError((error: HttpError) =>
            of(new actions.CreateMembershipFail(error)),
          ),
        );
      }),
    ),
  );

  updateMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.UPDATE_MEMBERSHIP),

      switchMap((action: actions.UpdateMembership) => {
        return this.membershipApiService
          .updateMembership(action.payload.updateMembershipData)
          .pipe(
            map((membership) => {
              return new actions.UpdateMembershipSuccess(membership);
            }),
            catchError((error: HttpError) =>
              of(new actions.UpdateMembershipFail(error)),
            ),
          );
      }),
    ),
  );

  // This effect is required for existing TC applications that haven't yet migrated to the new
  // facades in checkout component
  // TODO: Delete this when TCv2 and RCV2 has been migrated to use the new facades
  createOrUpdateMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.MembershipActionTypes.UPDATE_MEMBERSHIP_SUCCESS,
        actions.MembershipActionTypes.CREATE_MEMBERSHIP_SUCCESS,
        TCV2MembershipActionTypes.UPDATE_MEMBERSHIP_SUCCESS,
        TCV2MembershipActionTypes.CREATE_MEMBERSHIP_SUCCESS,
        RCV2MembershipActionType.UPDATE_MEMBERSHIP_SUCCESS,
        RCV2MembershipActionType.CREATE_MEMBERSHIP_SUCCESS,
        CreateRewardsProgramPreferenceSuccess,
      ),
      map(() => {
        return new actions.LoadAllMemberships();
      }),
    ),
  );

  pullMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RewardsPreferenceActionTypes.CREATE_REWARDS_PREFERENCE_SUCCESS),
      map(() => {
        return new actions.LoadAllMemberships();
      }),
    ),
  );

  pullMembershipOnUserLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserInit),
      map(() => {
        return new actions.LoadAllMemberships();
      }),
    ),
  );

  linkMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.linkMembership),
      switchMap((action) => {
        return this.membershipApiService
          .linkMembership(action.loyaltyProgramId)
          .pipe(
            map(({ url }) => actions.redirectToProgramLinking({ url })),
            catchError((error: HttpError) =>
              of(actions.linkMembershipFail({ error })),
            ),
          );
      }),
    ),
  );

  unlinkMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.unlinkMembership),
      switchMap((action) => {
        return this.membershipApiService
          .unlinkMembership(action.loyaltyProgramId)
          .pipe(
            map(({ url }) => actions.redirectToProgramLinking({ url })),
            catchError((error: HttpError) =>
              of(actions.unlinkMembershipFail({ error })),
            ),
          );
      }),
    ),
  );

  redirectToProgramLinking$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.redirectToProgramLinking),
        tap((action) => {
          this.guardhouseService.redirectToProgramLinkingUrl(
            `${action.url}?redirect_uri=${window.location.href}`,
          );
        }),
      ),
    {
      dispatch: false,
    },
  );
}
